import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isEqual, isNull } from "lodash";

import calendarGroupService from "@plannel/services/data/calendar-group-service";
import CalendarModal from "@plannel/components/modal/CalendarModal"
import PaginationContainer from "@plannel/components/PaginationContainer"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DataState from "@plannel/components/aggrid/DataState";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import reduxUtil from "@plannel/utils/redux-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "../utils/code-util";
import { grey } from "@mui/material/colors";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { calendarType, cycleUoms } = CodeUtil;
const CalendarGroupMaster = ({ t, title, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [calendarGroups, setCalendarGroups] = useState([]);

  const [sortParams, setSortParams] = useState({});
  const searchCode = useRef(reduxViewState?.searchCode ?? "");
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.page : 1);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [tPages, setTotalPages] = useState(0);

  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const selectedCalendar = useRef();

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: "calendarGrpGrid" })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calendarTypeFormatter = params => {
    const { value } = params;

    return calendarType?.[value] ? GridUtils.gridValueL10N(calendarType[value]) : value;
  }

  const columnDefs = [
    {
      headerName: "calendarGrpName", field: "name", width: 200, cellRenderer: "agGroupCellRenderer", filterType: 'string'
    },
    {
      headerName: "calendarType", field: "calType", width: 150,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      valueFormatter: calendarTypeFormatter,
      cellEditorParams: {
        valueFormatter: calendarTypeFormatter,
        values: Object.keys(calendarType)
      }, 
      filterType: 'string',
      filterOption: Object.keys(calendarType).map(key => key.toLowerCase())
    },
    { headerName: "descTxt", field: "descTxt", width: 200, flex: 1, filterType: 'string' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 80, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 170, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, width: 170, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  const detailEnabled = params => {
    return params.calendars ? params.calendars.length > 0 : false;
  }

  const disabledColor = { background: grey[300], opacity: 0.5 };

  const setWomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "M") && !data.domObj?.onDates) {
      return {};
    }

    return disabledColor;
  }

  const setDomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "W") || (isEqual(data.cycleCd, "M") && !data.domObj?.onDates)) {
      return {};
    }

    return disabledColor;
  }

  const valueFormatter = (params) => {
    return params.value?.name ? params.value.name : "";
  }

  const cycleCalendarColumnDefs = [
    { headerName: "calendarName", field: "name", type: ["nonEditableColumn"], width: 200 },
    {
      headerName: "repeatEvery", groupId: "repeatEvery",
      children: [
        {
          headerName: "", field: "intervalVal", type: ["numberColumn", "nonEditableColumn"], width: 90,
          cellStyle: params => {
            const { data } = params;
            if (isEqual(data.cycleCd, "S")) {
              return disabledColor;
            }
            return {};
          }
        },
        {
          headerName: "", field: "cycleCd", type: ["nonEditableColumn"], width: 100,
          cellClass: 'stringType',
          valueFormatter: params => {
            const { value } = params;
            const uomIndex = cycleUoms.findIndex(uom => isEqual(uom.value, value));
            if (uomIndex > -1) {
              const cycleUom = cycleUoms[uomIndex];
              return t(cycleUom.text);
            }

            return value;
          }
        }
      ]
    },
    {
      headerName: "period", groupId: "period",
      children: [
        { headerName: "startDate", field: "startDate", colId: "startDate", type: ["nonEditableColumn"], width: 120 },
        { headerName: "endDate", field: "endDate", colId: "endDate", type: ["nonEditableColumn"], width: 120 }
      ]
    },
    { headerName: "calendarGrpName", field: "calendarGroup", type: ["nonEditableColumn"], width: 150, valueFormatter },
    { headerName: "descTxt", field: "descTxt", type: ["nonEditableColumn"], width: 200 },
    // Month of Year
    {
      headerName: "monthOfYear", field: "domObj", type: ["nonEditableColumn"], width: 130,
      valueFormatter: params => {
        const { value } = params;
        if (value?.onMonth) {
          return value.onMonth + 1;
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y")) {
          return {};
        }
        return disabledColor;
      }
    },
    // Day of Month
    {
      headerName: "dateOfMonth", field: "domObj", type: ["nonEditableColumn"], width: 120,
      valueFormatter: params => {
        const { value } = params;

        if (value?.onDate) {
          return value.onDate;
        } else if (value?.onDates) {
          const dates = value.onDates;
          return dates.join(", ");
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y") || (isEqual(data.cycleCd, "M") && data.domObj?.onDates)) {
          return {};
        }
        return disabledColor;
      }
    },
    // Week of Month
    {
      headerName: "weekOfMonth", groupId: "wom",
      children: [
        { headerName: "first", field: "wom1wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "second", field: "wom2wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "third", field: "wom3wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "fourth", field: "wom4wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "fifth", field: "wom5wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
      ]
    },
    // Day of Week
    {
      headerName: "dayOfWeek", groupId: "dow",
      children: [
        { headerName: "mon", field: "dowMonFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "tue", field: "dowTueFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "wed", field: "dowWedFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "thu", field: "dowThuFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "fri", field: "dowFriFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "sat", field: "dowSatFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "sun", field: "dowSunFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
      ]
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn", "nonEditableColumn"], width: 80 }
  ];

  const onCellDoubleClicked = params => {
    selectedCalendar.current = params.data;
    setCalendarModalOpen(true);
  }

  const calendarGridDefaultSettings = DefaultGridSetting({ title, viewName, gridId: "calendarGrid" });

  const calendarGrid = {
    detailGridOptions: {
      ...calendarGridDefaultSettings,
      rowSelection: {
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false,
      },
      defaultColDef: {
        ...calendarGridDefaultSettings.defaultColDef,
        suppressHeaderMenuButton: true,
      },
      onCellDoubleClicked,
      columnDefs: GridUtils.getColumnDefs(cycleCalendarColumnDefs),
    },
    getDetailRowData: params => {
      params.successCallback(params.data.calendars);
    }
  };

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (searchCode.current) {
      searchFilters.calendarGrpName = searchCode.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const addRow = () => {
    const types = Object.keys(calendarType);
    const newRow = { id: null, calType: types[0], activeFlg: true, verNum: 0 };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const retrieveCalendarGroup = () => {
    const params = getRequestParams();
    calendarGroupService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setCalendarGroups(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {searchCode: searchCode.current, advancedFilters, currentPage, pageSize});
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const deleteCalendarGroup = () => {
    const gridApi = gridRef.current.api;
    gridApi.stopEditing();

    const calendarGrpIds = [];
    const deleteNodes = gridApi.getSelectedNodes();
    for (const node of deleteNodes) {
      if (node?.data?.id && !isNull(node.data.id)) {
        calendarGrpIds.push(node.data.id);
      }
    }

    if (!isEmpty(calendarGrpIds)) {
      calendarGroupService.remove(calendarGrpIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .finally(() => {
          retrieveCalendarGroup();
        });
    }

    if (!isEmpty(deleteNodes)) {
      DataState.deleteState(gridApi, deleteNodes);
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
    }
  }

  const saveCalendarGroup = () => {
    const gridApi = gridRef.current.api;
    gridApi.stopEditing();

    const allStateData = DataState.getAllStateData(gridApi);

    if (allStateData.length > 0) {
      calendarGroupService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .finally(() => {
          retrieveCalendarGroup();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const onGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true, gridId: "calendarGrpGrid" });
  }

  const handleDialog = action => {
    if (action === "DELETE") {
      deleteCalendarGroup();
    } else if (action === "SAVE") {
      saveCalendarGroup();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveCalendarGroup();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveCalendarGroup();
    }

    return () => {
      setCalendarGroups([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }

    return () => {
      setCalendarGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <TextFieldSearch sx={{ width: 250 }} label={"calendarGrpName"} searchCode={searchCode} retrieve={initPageToRetrieve} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={calendarGroupService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.calendarGrp, 'delete')}` })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="calendar-group-master-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={calendarGroups}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              masterDetail
              isRowMaster={detailEnabled}
              detailCellRendererParams={calendarGrid}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
        </Box>
        <PaginationContainer
          currentPage={currentPage} totalPages={tPages}
          pageSize={pageSize} pageSizes={pageSizes}
          setCurrentPage={setCurrentPage} setPageSize={setPageSize}
        />
        <CalendarModal
          readOnly
          modalOpen={calendarModalOpen}
          setModalOpen={setCalendarModalOpen}
          selectedCycle={selectedCalendar.current}
        />
        <Dialog
          open={dialogInfo.open}
          onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
          title={dialogInfo.title}
          content={dialogInfo.content}
          onHandler={handleDialog}
        />
        <Snackbar
          open={snackInfo.open}
          onClose={() => setSnackInfo({ ...snackInfo, open: false })}
          severity={snackInfo.severity}
          content={snackInfo.content}
        />
      </>
      );
};

      export default withTranslation()(CalendarGroupMaster);